import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserCheck, UserX, X as XIcon, MessageCircle, UserPlus, Send } from "lucide-react";
import Header from "@/components/Header";
import Sidebar from "@/components/Sidebar";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useMessages } from "@/contexts/MessagesContext";

const TABS = [
  { key: "friends",  label: "Friends",  icon: Users },
  { key: "incoming", label: "Incoming", icon: UserPlus },
  { key: "outgoing", label: "Outgoing", icon: Send },
];

function PersonRow({ person, right }) {
  return (
    <li className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] border-b border-[var(--tt-border)] last:border-b-0">
      <Link to={`/u/${person.user_id}`} className="flex items-center gap-3 flex-1 min-w-0">
        {person.picture ? (
          <img src={person.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center text-sm font-bold">
            {(person.name || "?")[0].toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate hover:text-[var(--tt-primary)]">{person.name}</div>
          {(person.requested_at || person.friends_since) && (
            <div className="text-[10px] text-[var(--tt-text-faint)] font-mono-tt">
              {person.friends_since ? `Friends since ${person.friends_since.slice(0, 10)}`
                                    : `Requested ${person.requested_at?.slice(0, 10)}`}
            </div>
          )}
        </div>
      </Link>
      <div className="flex items-center gap-2 flex-wrap">{right}</div>
    </li>
  );
}

export default function FriendsPage() {
  const { user } = useAuth();
  const { openWith } = useMessages();
  const [tab, setTab] = useState("friends");
  const [data, setData] = useState({ friends: [], incoming: [], outgoing: [] });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/friends"),
        api.get("/friends/pending"),
        api.get("/friends/sent"),
      ]);
      setData({
        friends:  Array.isArray(a.data) ? a.data : [],
        incoming: Array.isArray(b.data) ? b.data : [],
        outgoing: Array.isArray(c.data) ? c.data : [],
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.user_id]);

  const accept = async (uid) => {
    try { await api.post(`/friends/accept/${uid}`); toast.success("Accepted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const reject = async (uid) => {
    try { await api.post(`/friends/reject/${uid}`); toast.success("Rejected"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const removeOrCancel = async (uid) => {
    try { await api.delete(`/friends/${uid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const current = data[tab] || [];

  return (
    <div className="min-h-screen bg-[var(--tt-bg)] text-[var(--tt-text)]">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 min-w-0">
          <Header />
          <main className="px-6 md:px-10 py-8 max-w-4xl">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-6 h-6 text-[var(--tt-primary)]" />
              <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Friends</h1>
            </div>
            <p className="text-sm text-[var(--tt-text-muted)] mb-6">
              Manage requests, accept new friends, and start direct conversations.
            </p>

            <div className="flex items-center gap-2 border-b border-[var(--tt-border)] mb-6">
              {TABS.map((t) => {
                const TabIcon = t.icon;
                const count = data[t.key]?.length || 0;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`friends-tab-${t.key}`}
                    className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors
                      ${active
                        ? "border-[var(--tt-primary)] text-[var(--tt-primary)]"
                        : "border-transparent text-[var(--tt-text-muted)] hover:text-white"}`}
                  >
                    <TabIcon className="w-3.5 h-3.5" />
                    {t.label}
                    {count > 0 && (
                      <span className="text-[10px] font-mono-tt bg-[var(--tt-surface-2)] text-white/80 px-1.5 py-0.5 rounded-full">{count}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
              {loading ? (
                <div className="px-6 py-12 text-center text-sm text-[var(--tt-text-muted)]">Loading...</div>
              ) : current.length === 0 ? (
                <div className="px-6 py-16 text-center text-sm text-[var(--tt-text-muted)]">
                  {tab === "friends" && "You have no friends yet. Visit a user profile and click Add Friend."}
                  {tab === "incoming" && "No pending requests."}
                  {tab === "outgoing" && "No outgoing requests."}
                </div>
              ) : (
                <ul data-testid={`friends-list-${tab}`}>
                  {current.map((p) => (
                    <PersonRow
                      key={p.user_id}
                      person={p}
                      right={
                        tab === "friends" ? (
                          <>
                            <button
                              onClick={() => openWith(p)}
                              data-testid={`friends-message-${p.user_id}`}
                              className="inline-flex items-center gap-1.5 bg-[var(--tt-surface-2)] hover:bg-[#222] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[var(--tt-border)] hover:border-[var(--tt-primary)]"
                            >
                              <MessageCircle className="w-3 h-3 text-[var(--tt-purple)]" />
                              Message
                            </button>
                            <button
                              onClick={() => removeOrCancel(p.user_id)}
                              data-testid={`friends-remove-${p.user_id}`}
                              className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-red-500/15 text-white hover:text-red-300 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[var(--tt-border)] hover:border-red-500/40"
                            >
                              <UserX className="w-3 h-3" />
                              Remove
                            </button>
                          </>
                        ) : tab === "incoming" ? (
                          <>
                            <button
                              onClick={() => accept(p.user_id)}
                              data-testid={`friends-accept-${p.user_id}`}
                              className="inline-flex items-center gap-1.5 bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full"
                            >
                              <UserCheck className="w-3 h-3" /> Accept
                            </button>
                            <button
                              onClick={() => reject(p.user_id)}
                              data-testid={`friends-reject-${p.user_id}`}
                              className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[var(--tt-border)]"
                            >
                              <UserX className="w-3 h-3" /> Reject
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => removeOrCancel(p.user_id)}
                            data-testid={`friends-cancel-${p.user_id}`}
                            className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[var(--tt-border)]"
                          >
                            <XIcon className="w-3 h-3" /> Cancel
                          </button>
                        )
                      }
                    />
                  ))}
                </ul>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
